import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Eye, EyeOff } from "lucide-react";
import { TopBar } from "../components/TopBar";
import { AppShell } from "../components/Shell";
import { useAuth } from "../context/AuthContext";
import { errorMessage } from "../lib/api";
import { CityAutocomplete } from "../components/CityAutocomplete";
import { useT } from "../i18n";

export default function Signup() {
  const { register } = useAuth();
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", city: "", email: "", password: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await register(form);
      navigate("/onboarding", { state: { first_name: form.first_name, city: form.city } });
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AppShell narrow>
      <TopBar compact onBack={() => navigate("/")} />
      <main className="px-5 pb-12 pt-6 stagger" data-testid="signup-page">
        <h1 className="h-display text-5xl">
          {t("signup_h1a")}
          <br />
          <span className="text-rox-orange">{t("signup_h1b")}</span>
        </h1>
        <p className="mt-5 text-base text-rox-off/80">{t("signup_sub")}</p>
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-2 block text-sm font-medium">{t("first_name")}</span>
              <input data-testid="signup-first-name" className="field" placeholder="Thomas" value={form.first_name} onChange={set("first_name")} required maxLength={40} />
            </label>
            <label className="block">
              <span className="mb-2 block text-sm font-medium">{t("last_name")}</span>
              <input data-testid="signup-last-name" className="field" placeholder="Martin" value={form.last_name} onChange={set("last_name")} required maxLength={40} />
            </label>
          </div>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">{t("city")}</span>
            <CityAutocomplete value={form.city} onSelect={({ city }) => setForm((f) => ({ ...f, city }))} testId="signup-city" required />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">{t("email")}</span>
            <input data-testid="signup-email" className="field" type="email" placeholder="thomas@email.com" value={form.email} onChange={set("email")} required />
          </label>
          <label className="block">
            <span className="mb-2 block text-sm font-medium">{t("password")}</span>
            <div className="relative">
              <input data-testid="signup-password" className="field pr-12" type={show ? "text" : "password"} placeholder={t("pwd_hint")} value={form.password} onChange={set("password")} required minLength={6} />
              <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-rox-muted hover:text-rox-off" aria-label={t("a_show_pwd")}>
                {show ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </label>
          {error && (
            <p data-testid="signup-error" className="rounded-[4px] border border-rox-orange/50 bg-rox-orange/10 px-3 py-2 text-sm text-rox-orange">{error}</p>
          )}
          <button data-testid="signup-submit" className="btn-primary" disabled={busy}>
            {busy ? "..." : t("create_profile")} <ArrowRight size={20} />
          </button>
        </form>
        <p className="mt-8 text-center text-sm text-rox-off/70">
          <Link to="/connexion" data-testid="signup-to-login" className="font-display uppercase tracking-wide text-rox-orange">{t("have_account")}</Link>
        </p>
      </main>
    </AppShell>
  );
}
